// NPM Imports
import React, { useEffect } from 'react'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
// JS Imports
import {
   //@ts-ignore
   formatFloatAsPercent,
   //@ts-ignore
   formatFloatAsCurrencySymbol,
   //@ts-ignore
   IsForeignCurrencyQuote,
   //@ts-ignore
   isAlwaysUSD,
} from '../../../common/helpers.mjs'
// Asset Imports
//@ts-ignore
import { ProductInputType } from '../../../common/types/types.ts'
//@ts-ignore
import {
   isProductOutsideTermRange,
   formatTermRange,
   //@ts-ignore
} from '../../../js/validate.jsx'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
// Component Imports
import Description from '../../ui-helper-elements/Description'
// Type imports
//@ts-ignore
import type { Product } from '../../../common/types/types'

interface DynamicProductRowProps {
   productCode: string
}

interface DynamicProductInputRowProps {
   productCode: string
   disabled?: boolean
}

function BooleanRow({
   productCode,
   disabled = false,
}: DynamicProductInputRowProps) {
   const state = useQuoteContext()
   return (
      <input
         type='checkbox'
         checked={state.dynamicProducts.get(productCode) !== 0}
         disabled={disabled}
         onChange={(e) =>
            state.setDynamicProduct(productCode, e.target.checked ? 1 : 0)
         }
      />
   )
}

function IntegerRow({
   productCode,
   disabled = false,
}: DynamicProductInputRowProps) {
   const state = useQuoteContext()
   return (
      <input
         className='form-control'
         placeholder={'0'}
         type='number'
         step={1}
         disabled={disabled}
         value={
            (state.dynamicProducts?.get(productCode) ?? 0) <= 0
               ? ''
               : Math.floor(state.dynamicProducts?.get(productCode) ?? 0)
         }
         onChange={(e) =>
            state.setDynamicProduct(
               productCode,
               Math.floor(Number(e.target.value)),
            )
         }
         onBlur={() =>
            state.setDynamicProduct(
               productCode,
               (state.dynamicProducts?.get(productCode) ?? 0) < 0.01
                  ? 0
                  : Math.floor(state.dynamicProducts?.get(productCode) ?? 0),
            )
         }
      />
   )
}

function DecimalRow({
   productCode,
   disabled = false,
}: DynamicProductInputRowProps) {
   const state = useQuoteContext()
   return (
      <input
         className='form-control'
         type='number'
         step={0.01}
         disabled={disabled}
         value={
            (state.dynamicProducts.get(productCode) as number) <= 0
               ? ''
               : (state.dynamicProducts.get(productCode) as number)
         }
         onChange={(e) =>
            state.setDynamicProduct(productCode, Number(e.target.value))
         }
         onBlur={() =>
            state.setDynamicProduct(
               productCode,
               (state.dynamicProducts.get(productCode) as number) < 0.01
                  ? 0
                  : Math.floor(
                       (state.dynamicProducts.get(productCode) as number) * 100,
                    ) / 100,
            )
         }
      />
   )
}

function DynamicMonthlyProductRow({ productCode }: DynamicProductRowProps) {
   const state = useQuoteContext()

   // Initialize the product in the map if it doesn't exist
   // Must be in useEffect to avoid setState during render
   useEffect(() => {
      if (state.dynamicProducts.get(productCode) === undefined) {
         state.setDynamicProduct(productCode, 0)
      }
   }, [productCode, state.dynamicProducts, state.setDynamicProduct])

   if (state.product_list[productCode]?.NotInPricebook) {
      return <> </>
   }
   const product = state.product_list[productCode]

   // Use USD currency object for AlwaysUSD products on foreign currency quotes
   const currencyObj =
      IsForeignCurrencyQuote(state.pricebookId) && isAlwaysUSD(productCode)
         ? state.currencyObjectUSD
         : state.currencyObject

   // DependsOn: auto-select/deselect based on dependency product
   const dependsOnCode = product?.DependsOn
   const dependencyValue = dependsOnCode
      ? (state.dynamicProducts.get(dependsOnCode) ?? 0)
      : 0
   const dependencyActive = !!dependsOnCode && dependencyValue > 0

   useEffect(() => {
      if (!dependsOnCode) return
      if (dependencyValue > 0) {
         // Auto-select when dependency is active
         if ((state.dynamicProducts.get(productCode) ?? 0) === 0) {
            state.setDynamicProduct(productCode, 1)
         }
      } else {
         // Force off when dependency is inactive
         if ((state.dynamicProducts.get(productCode) ?? 0) !== 0) {
            state.setDynamicProduct(productCode, 0)
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dependsOnCode, dependencyValue, productCode])

   // Determine if input should be disabled based on DependsOn and OverrideRoles
   const canOverride = product?.OverrideRoles?.has(state.user_role) ?? false
   const dependsOnDisabled =
      !!dependsOnCode &&
      (!dependencyActive || (dependencyActive && !canOverride))
   const outsideTermRange = isProductOutsideTermRange(product, state.termLength)
   const isDisabled = dependsOnDisabled || outsideTermRange
   const permissions = product?.RoleRequired?.split(',') ?? []
   const hasPermissions =
      (permissions?.length > 0 &&
         (permissions?.includes(state.user_role) ?? true)) ||
      permissions?.length === 0
   const product_type = product?.InputType
   const productCost = state.selectedProductCosts[productCode]
   let hasProduct = false

   if (
      (product_type === ProductInputType.Integer ||
         product_type === ProductInputType.Decimal) &&
      (state.dynamicProducts.get(productCode) as number) > 0
   ) {
      hasProduct = true
   } else if (
      product_type === ProductInputType.Boolean &&
      (state?.dynamicProducts?.get(productCode) ?? 0) > 0
   ) {
      hasProduct = true
   }

   // Check if we should render the component
   if (hasPermissions || hasProduct) {
      return (
         <div className='row'>
            <div className='col-bhr1'>
               {product.ProductDesc ? (
                  <Description
                     title={product.ProductName}
                     className={'width-text'}
                     content={<>{product.ProductDesc}</>}
                  >
                     {product.ProductName}
                  </Description>
               ) : (
                  product.ProductName
               )}{' '}
               (
               {formatFloatAsCurrencySymbol(
                  product_type === ProductInputType.Boolean && !product?.IsPEPM
                     ? (product?.BasePrice ?? 0)
                     : (product?.EmpListPrice ?? 0),
                  currencyObj,
               )}
               )
            </div>
            <div className='col-bhr2'>
               {(() => {
                  const inputEl =
                     product_type === ProductInputType.Boolean ? (
                        <BooleanRow
                           productCode={productCode}
                           disabled={isDisabled}
                        />
                     ) : product_type === ProductInputType.Integer ? (
                        <IntegerRow
                           productCode={productCode}
                           disabled={isDisabled}
                        />
                     ) : (
                        <DecimalRow
                           productCode={productCode}
                           disabled={isDisabled}
                        />
                     )
                  if (!outsideTermRange) return inputEl
                  return (
                     <OverlayTrigger
                        delay={{ show: 250, hide: 150 }}
                        placement='top'
                        overlay={
                           <Tooltip id={`term-range-tooltip-${productCode}`}>
                              {formatTermRange(product)}
                           </Tooltip>
                        }
                     >
                        <span style={{ display: 'inline-block' }}>
                           {inputEl}
                        </span>
                     </OverlayTrigger>
                  )
               })()}
            </div>
            <div className='col'>
               {formatFloatAsPercent(
                  productCost?.volumeDiscountedPEPMPercent ?? 0,
               )}
            </div>
            <div className='col'>
               {state.hasUSEmployees == true ||
               (state.user_view == 'Internal' && product?.IsPEPM)
                  ? formatFloatAsPercent(
                       productCost?.bundleDiscountedPEPMPercent ?? 0,
                    )
                  : ''}
            </div>
            <div className='col'>
               {product?.IsPEPM &&
               (productCost?.discountedPEPMCost ?? 0) <=
                  (productCost?.listPEPMCost ?? 0)
                  ? formatFloatAsCurrencySymbol(
                       productCost?.discountedPEPMCost ?? 0,
                       currencyObj,
                    )
                  : ''}
            </div>
            <div className='col'>
               {formatFloatAsCurrencySymbol(
                  productCost?.totalPrice ?? 0,
                  currencyObj,
               )}
            </div>
            {state.differenceQuote == true ? (
               <>
                  <div className='col col-bhr-blue'>
                     {/* {state.MonthlyTaxCosts.previousTotalPrice > 0 &&
                            formatFloatAsCurrencySymbol(state.MonthlyTaxCosts.previousTotalPrice, state.currencyObject)
                        } */}
                  </div>
                  <span className='col'>
                     {/* {formatFloatAsCurrencySymbol(state.MonthlyTaxCosts.totalPriceDifference || 0, state.currencyObject)} */}
                  </span>
                  {process.env.REACT_APP_ENVIRONMENT == 'staging' && (
                     <div className='col'>
                        {/* {formatFloatAsCurrencySymbol(state.MonthlyTaxCosts.MRRTotal || 0, state.currencyObject)} */}
                     </div>
                  )}
               </>
            ) : (
               <></>
            )}
         </div>
      )
   } else {
      return <></>
   }
}

export default function DynamicMonthlyProductRows() {
   const state = useQuoteContext()
   const showOnScreen = (
      product: Product,
      value: number,
      user_view: string,
   ) => {
      if (value > 0) {
         return true
      } else {
         return product.ShowOnScreen.has(user_view)
      }
   }
   const dynamicMonthlyProductCodes = Object.keys(state.product_list).filter(
      (key) =>
         state.product_list[key].Dynamic &&
         state.product_list[key].BillingFreq === 'monthly' &&
         state.product_list[key].ProductCode !== 'timeAttendance' &&
         showOnScreen(
            state.product_list[key],
            state.dynamicProducts.get(key) ?? 0,
            state.user_view,
         ),
   )
   // Sort dynamic products by DisplayOrder and map to components
   const dynamicMonthlyProductRows = dynamicMonthlyProductCodes
      .sort((a, b) => {
         const orderA = state.product_list[a]?.DisplayOrder || 0
         const orderB = state.product_list[b]?.DisplayOrder || 0
         return orderA - orderB
      })
      .map((productCode) => (
         <DynamicMonthlyProductRow
            key={productCode}
            productCode={productCode}
         />
      ))
   return <>{dynamicMonthlyProductRows}</>
}
